/**
 * Badge colorato per lo stato di una fattura SDI.
 *
 * Usa esclusivamente i token di @floraltech/ui per colori e stile.
 * Nessun colore hardcoded (DEC-015, regola 6.8 CLAUDE.md).
 */

import { Badge } from '@floraltech/ui';
import React from 'react';

/** Mappa stato → variante badge (usa le varianti definite in ui_core). */
const STATO_VARIANTE: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  bozza: 'secondary',
  in_invio: 'default',
  inviata: 'default',
  consegnata: 'success',
  // Giallo, non verde: la fattura e' valida ma il cliente NON l'ha ricevuta e
  // l'avviso tocca a noi. Verde direbbe "tutto a posto", e non lo e'.
  non_consegnata: 'warning',
  trasmessa: 'default',
  accettata: 'success',
  rifiutata: 'destructive',
  scartata: 'destructive',
  ricevuta: 'success',
  errore: 'destructive',
};

/** Mappa stato → etichetta leggibile in italiano. */
const STATO_LABEL: Record<string, string> = {
  bozza: 'Bozza',
  in_invio: 'In invio',
  inviata: 'Inviata',
  consegnata: 'Consegnata',
  non_consegnata: 'Non consegnata',
  trasmessa: 'Trasmessa',
  // Accettazione e rifiuto esistono SOLO verso la Pubblica Amministrazione:
  // etichettati per non farli scambiare per esiti di una fattura B2B.
  accettata: 'Accettata (PA)',
  rifiutata: 'Rifiutata (PA)',
  scartata: 'Scartata',
  ricevuta: 'Ricevuta',
  errore: 'Errore',
};

interface StatoBadgeProps {
  /** Stato della fattura (es. 'bozza', 'accettata', 'rifiutata'). */
  stato: string;
  /** Classe CSS aggiuntiva opzionale. */
  className?: string;
}

/**
 * Badge che mostra lo stato di una fattura con colore semantico.
 *
 * @param stato - Stato della fattura.
 * @param className - Classe CSS aggiuntiva.
 */
const StatoBadge: React.FC<StatoBadgeProps> = ({ stato, className }) => {
  const variante = STATO_VARIANTE[stato] ?? 'secondary';
  const label = STATO_LABEL[stato] ?? stato;

  return (
    <Badge variant={variante} className={className}>
      {label}
    </Badge>
  );
};

export default StatoBadge;
